// Pure helpers for the DeltaSlider. Kept out of the component so the
// delta-bar maths and the readout formatting are unit-testable without a
// DOM or React Testing Library.

/** Format a slider value for the readout: a leading "+" for positive
 *  values (negatives already carry "-"), then the value and the optional
 *  unit suffix. E.g. formatDelta(20, "%") === "+20%". */
export function formatDelta(value: number, postfix = ""): string {
  return `${value > 0 ? "+" : ""}${value}${postfix}`;
}

/** The fill span (in track percent) painted between the default/zero
 *  position and the current value. fillStart === fillEnd at the default,
 *  so the bar collapses to nothing. */
export function deltaBarFill(
  min: number,
  max: number,
  defaultValue: number,
  value: number,
): { fillStart: number; fillEnd: number } {
  const range = max - min;
  const zeroPercent = ((defaultValue - min) / range) * 100;
  const valuePercent = ((value - min) / range) * 100;
  return {
    fillStart: Math.min(zeroPercent, valuePercent),
    fillEnd: Math.max(zeroPercent, valuePercent),
  };
}

/** The inline `background` for the range track: brand-primary fill between
 *  the default position and the current value, neutral either side. */
export function deltaBarBackground(
  min: number,
  max: number,
  defaultValue: number,
  value: number,
): string {
  // Teal fill ($beamm-control / #1eb7ba) — matches the bslib rate sliders and
  // breaks up the purple-dominant interface. The thumb and the value readout
  // stay brand purple (the thumb as the grabbable handle; purple text for AA
  // contrast — the teal fails contrast as text on white).
  const { fillStart, fillEnd } = deltaBarFill(min, max, defaultValue, value);
  return `linear-gradient(
        to right,
        #e0dce8 0%,
        #e0dce8 ${fillStart}%,
        #1eb7ba ${fillStart}%,
        #1eb7ba ${fillEnd}%,
        #e0dce8 ${fillEnd}%,
        #e0dce8 100%
      )`;
}
